import { useState, useRef, useEffect } from 'react';
import type { AnalysisOption, AnalysisType } from '../../shared/types';
import { useAuth } from '../contexts/AuthContext';
import type { PlanFeatures } from '../../shared/types';
import AnalysisIcon from './ui/AnalysisIcons';
import { analysisTint } from '../utils/tints';

interface Props {
  options: AnalysisOption[];
  onSelect: (option: AnalysisOption) => void;
  onUpgrade?: () => void;
  onUnitPurchase?: () => void;
}

// `adminOnly` hides the option entirely from non-super-admins. Used for
// features that exist in code but are not finished enough to ship publicly,
// even to paying tiers — e.g. Visaly Créa is being kept admin-only until ready.
const featureMap: Record<string, { feature: keyof PlanFeatures; requiredPlan: 'Pro' | 'Premium'; adminOnly?: boolean }> = {
  brand_name: { feature: 'analysesText', requiredPlan: 'Pro' },
  slogan: { feature: 'analysesText', requiredPlan: 'Pro' },
  concept: { feature: 'analysesText', requiredPlan: 'Pro' },
  general_question: { feature: 'analysesText', requiredPlan: 'Pro' },
  label: { feature: 'analysesLabel', requiredPlan: 'Pro' },
  campaign: { feature: 'analysesCampaign', requiredPlan: 'Premium' },
  social_post: { feature: 'analysesSocial', requiredPlan: 'Premium' },
  creative_brief: { feature: 'analysesCreative', requiredPlan: 'Premium', adminOnly: true },
  event_activation: { feature: 'analysesEvent', requiredPlan: 'Premium' },
};

/* Recul de la grille avant de céder la place — aligné sur `.animate-grid-recede`. */
const LEAVE_MS = 200;

/* Position du curseur écrite en variables CSS sur la carte survolée : le
 * halo radial de `.glow-card::after` se recentre en continu sous la souris.
 * Écriture directe dans le style de l'élément — aucun re-render React. */
function trackPointer(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  const r = el.getBoundingClientRect();
  el.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`);
  el.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`);
}

/* Au départ du curseur, on ramène doucement le halo à sa position de repos
 * pour que la carte suivante ne « saute » pas. */
function resetPointer(e: React.MouseEvent<HTMLElement>) {
  const el = e.currentTarget;
  el.style.removeProperty('--mx');
  el.style.removeProperty('--my');
}


export default function AnalysisTypeSelector({ options, onSelect, onUpgrade, onUnitPurchase }: Props) {
  const { features, organization, user, previewPlan } = useAuth();

  /* ── Ouverture d'une analyse ────────────────────────────────────────
     La grille recule brièvement, puis l'écran d'analyse arrive avec son
     `animate-app-in`. Rien d'autre : la continuité visuelle est portée par
     la COULEUR — la teinte de la tuile se retrouve sur le bandeau d'en-tête
     et sur les champs de l'écran de destination.

     (Il y avait ici un clone plein cadre qui s'étalait puis se dissolvait.
     Il n'avait de sens que quand l'écran d'analyse était un aplat plein :
     depuis le retour au fond crème, il ne faisait plus que produire un
     rectangle coloré qui apparaît et disparaît. Supprimé.) */
  const [leaving, setLeaving] = useState(false);
  const timers = useRef<number[]>([]);

  // Les minuteries doivent mourir avec le composant : sans ça, un retour
  // arrière pendant l'animation déclencherait `onSelect` sur un écran démonté.
  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const openAnalysis = (option: AnalysisOption) => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { onSelect(option); return; }

    setLeaving(true);
    timers.current.push(window.setTimeout(() => onSelect(option), LEAVE_MS));
  };

  const isBeta = organization?.plan === 'beta';
  // Treat the admin as a regular user when they're previewing another plan,
  // so the preview reflects exactly what that plan would see.
  const isSuperAdmin = user?.isSuperAdmin === true && previewPlan === null;

  const visibleOptions = options.filter((o) => {
    // Visaly Créa est masqué pour tous (feature mise en pause, non développée pour
    // l'instant). Le bloc « sur mesure » ci-dessous le remplace dans la grille.
    if (o.key === 'creative_brief') return false;
    const entry = featureMap[o.key];
    return entry?.adminOnly ? isSuperAdmin : true;
  });

  // Crédits bundle/à-la-carte disponibles par type — débloquent une tuile même
  // quand le plan ne donne pas accès au type (aligné sur le bypass org_credits
  // côté backend, requireAnalysisAccess Path 3).
  const creditsFor = (key: AnalysisType) => organization?.creditsPerType?.[key] ?? 0;

  const planGrants = (key: AnalysisType) => {
    const entry = featureMap[key];
    return entry ? !!features[entry.feature] : true;
  };

  // Verrouillé seulement si NI le plan NI un crédit ne donnent accès.
  const isLocked = (key: AnalysisType) => !planGrants(key) && creditsFor(key) <= 0;

  // Débloqué spécifiquement par un crédit acheté → on affiche un compteur, pas le cadenas.
  const creditUnlocked = (key: AnalysisType) => !planGrants(key) && creditsFor(key) > 0;

  const requiredPlanFor = (key: AnalysisType) => featureMap[key]?.requiredPlan ?? 'Pro';

  // Free / beta tier: show remaining analyses.
  // Un acheteur de crédits/bundle (crédits dispo, pas d'allocation gratuite)
  // n'est PAS concerné par ce bandeau « free » — il a déjà payé ; on l'affiche
  // donc seulement s'il existe une allocation gratuite (ou plan beta).
  const freeLimit = organization?.freeAnalysesLimit ?? 0;
  const hasCredits = (organization?.creditBalance ?? 0) > 0;
  const freeRemaining = (!features.unlimited && organization && !(hasCredits && freeLimit === 0))
    ? Math.max(0, freeLimit - (organization.freeAnalysesUsed ?? 0))
    : null;

  return (
    <div className="animate-app-in">
      <div className="mb-9">
        <h2 className="font-display mb-2 text-[2.15rem] leading-[1.1] text-slate-900 sm:text-[2.6rem]">
          Que souhaitez-vous analyser&nbsp;?
        </h2>
        <p className="max-w-xl text-slate-500">
          &Eacute;valuez votre communication et obtenez un pr&eacute;-scoring de conformit&eacute; au regard de la r&eacute;glementation de la publicit&eacute; des produits de sant&eacute;.
        </p>
        {freeRemaining !== null && (
          <div className="mt-4 flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:gap-3">
            <span className={`text-sm font-semibold ${freeRemaining > 0 ? (isBeta ? 'text-emerald-600' : 'text-amber-600') : 'text-red-600'}`}>
              {freeRemaining > 0
                ? isBeta
                  ? `${freeRemaining} analyse${freeRemaining > 1 ? 's' : ''} Beta Partner restante${freeRemaining > 1 ? 's' : ''}`
                  : `${freeRemaining} analyse${freeRemaining > 1 ? 's' : ''} gratuite${freeRemaining > 1 ? 's' : ''} restante${freeRemaining > 1 ? 's' : ''} ce mois-ci`
                : isBeta
                  ? 'Vous avez utilisé vos analyses Beta Partner'
                  : freeLimit === 0
                    ? 'Aucune analyse incluse — abonnez-vous ou achetez un bundle'
                    : 'Vous avez utilisé vos analyses gratuites ce mois-ci'}
            </span>
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-1.5 rounded-full bg-ink px-3.5 py-1.5 text-xs font-semibold text-white transition-transform hover:scale-[1.03]"
            >
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 0 0-2.455 2.456Z" />
              </svg>
              {isBeta ? 'Beta Partner' : 'Offre gratuite'}
            </button>
          </div>
        )}

        {/* À la carte hint — contextual, only for free users (not beta) */}
        {freeRemaining !== null && !isBeta && (
          freeRemaining > 0 ? (
            <p className="mt-3 text-sm text-slate-400">
              Besoin ponctuel ?{' '}
              <button onClick={onUnitPurchase ?? onUpgrade} className="underline underline-offset-2 hover:text-violet-600 transition-colors">
                Achetez un bundle dès 29&nbsp;€, sans abonnement&nbsp;→
              </button>
            </p>
          ) : (
            <div className="mt-4 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-violet-50 border border-violet-200 max-w-md mx-auto">
              <svg className="w-4 h-4 text-violet-500 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 0 0-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 0 0-16.536-1.84M7.5 14.25 5.106 5.272M6 20.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Zm12.75 0a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z" />
              </svg>
              <span className="text-sm text-violet-700">
                Pas prêt pour un abonnement ?{' '}
                <button onClick={onUnitPurchase ?? onUpgrade} className="font-semibold underline underline-offset-2 hover:text-violet-900 transition-colors">
                  Achetez un bundle dès 29&nbsp;€
                </button>
              </span>
            </div>
          )
        )}
      </div>

      <div className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 ${leaving ? 'animate-grid-recede' : 'stagger-children'}`}>
        {visibleOptions.map((option) => {
          const locked = isLocked(option.key);
          return (
            <button
              key={option.key}
              onClick={() => {
                if (locked) { onUpgrade?.(); return; }
                openAnalysis(option);
              }}
              onMouseMove={trackPointer}
              onMouseLeave={resetPointer}
              className={`glow-card group flex flex-col rounded-3xl p-6 text-left ${
                locked
                  ? 'tint-neutral cursor-default opacity-55'
                  : analysisTint(option.key).className
              }`}
            >
              <div className="mb-4 flex items-start justify-between">
                {/* Pastille d'icône blanche — le motif rond de la charte */}
                <span className="grid h-11 w-11 place-items-center rounded-full bg-white/60">
                  <AnalysisIcon type={option.key} className="h-5 w-5 text-ink" />
                </span>
                {locked ? (
                  <span className="flex items-center gap-1 rounded-full bg-ink px-2.5 py-1 text-xs font-medium text-white">
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6}>
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    {requiredPlanFor(option.key)}
                  </span>
                ) : creditUnlocked(option.key) ? (
                  <span className="flex items-center gap-1 rounded-full bg-white/70 px-2.5 py-1 text-xs font-medium text-ink">
                    <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                    {creditsFor(option.key)} crédit{creditsFor(option.key) > 1 ? 's' : ''}
                  </span>
                ) : null}
              </div>
              <h3 className="font-display mb-1.5 text-[1.05rem] text-ink">
                {option.label}
              </h3>
              <p className="text-sm leading-relaxed text-ink/65">
                {option.description}
              </p>
              <div className="mt-auto flex items-center gap-1.5 pt-4 text-xs text-ink/45">
                {option.inputType === 'text' ? 'Saisie texte' : option.inputType === 'text+image' ? 'Texte + images' : 'Upload image(s)'}
                <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          );
        })}

        {/* Bloc sur mesure — CTA non-analyse : ouvre un mail pré-rempli pour
            commander un outil/feature personnalisé (réglementaire ou non). */}
        <button
          onClick={() => {
            const subject = 'Demande de bloc sur mesure — Visaly';
            const body = [
              'Bonjour,',
              '',
              'Je souhaite créer un bloc / outil sur mesure pour mes besoins.',
              '',
              'Type d\'outil envisagé (checker de packaging, chatbot, automatisation IA, autre) :',
              'Description du besoin :',
              'Filière / secteur :',
              '',
              'Merci !',
            ].join('\n');
            window.location.href = `mailto:ludovic@blackthornsdesign.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
          }}
          onMouseMove={trackPointer}
          onMouseLeave={resetPointer}
          className="glow-card tint-ink group flex flex-col rounded-3xl p-6 text-left"
        >
          <div className="mb-4 flex items-start justify-between">
            <span className="grid h-11 w-11 place-items-center rounded-full bg-white/12">
              <svg className="h-5 w-5 text-rose-pastel-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" />
              </svg>
            </span>
          </div>
          <h3 className="font-display mb-1.5 text-[1.05rem] text-white">
            Créez votre bloc sur mesure
          </h3>
          <p className="text-sm leading-relaxed text-white/60">
            Besoin d'un outil sur mesure, réglementaire ou non ? Checker de packaging, chatbot, automatisation IA… Nous le construisons pour vous.
          </p>
          <div className="mt-auto inline-flex items-center gap-1.5 pt-4 text-xs font-semibold text-rose-pastel-400">
            Contactez-nous
            <svg className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.6} strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>
    </div>
  );
}
